import { useEffect, useState } from "react";
import { toast } from 'react-toastify';
import { getAiReviewSettings, saveAiReviewSettings } from "../../../api/challenge";

const MIN = 70;
const MAX = 100;

const Switch = ({ on, onChange, label }) => (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors"
        style={{ backgroundColor: on ? '#25a55f' : '#b8bcc4' }}>
        <span className="inline-block h-5 w-5 rounded-full shadow transition-transform"
            style={{ backgroundColor: '#fff', transform: `translateX(${on ? 22 : 2}px)` }} />
    </button>
);

const Row = ({ title, hint, value, onChange }) => (
    <div className="flex flex-wrap items-center gap-3 py-2">
        <Switch on={value.enabled} onChange={(enabled) => onChange({ ...value, enabled })} label={title} />
        <span className="font-semibold w-40">{title}: {value.enabled ? 'ON' : 'OFF'}</span>
        <label className="flex items-center gap-2 text-sm">
            Threshold
            <input type="number" min={MIN} max={MAX} step={1} value={value.threshold}
                onChange={(e) => onChange({ ...value, threshold: e.target.value === '' ? '' : Number(e.target.value) })}
                className="w-20 px-2 py-1 border border-gray-300 rounded" aria-label={`${title} threshold`} />
            %
        </label>
        <span className="text-xs text-gray-600">{hint}</span>
    </div>
);

const fmt = (d) => d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' IST' : '';

const AiReviewSettings = () => {
    const [saved, setSaved] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const [note, setNote] = useState(null); // { ok, text } shown next to Save

    useEffect(() => {
        getAiReviewSettings()
            .then((s) => { setSaved(s); setForm({ approve: s.approve, reject: s.reject }); })
            .catch((e) => setError(e?.response?.data?.error || "Could not load the AI review settings."));
    }, []);

    const badThreshold = form && [form.approve, form.reject].some((v) => !Number.isInteger(v.threshold) || v.threshold < MIN || v.threshold > MAX);
    const changed = form && saved && JSON.stringify({ approve: form.approve, reject: form.reject }) !== JSON.stringify({ approve: saved.approve, reject: saved.reject });

    const save = async () => {
        setSaving(true); setNote(null);
        try {
            const s = await saveAiReviewSettings(form);
            setSaved(s); setForm({ approve: s.approve, reject: s.reject });
            setNote({ ok: true, text: "Saved. Applies to the next submissions the AI reviews." });
            toast.success("AI review settings saved.");
        } catch (e) {
            const text = e?.response?.data?.error || "Could not save the AI review settings.";
            setNote({ ok: false, text });
            toast.error(text);
        } finally { setSaving(false); }
    };

    return (
        <div className="mb-4 px-4 py-3 rounded-md border border-[#e0a800] bg-[#fff8e1] text-gray-900">
            <p className="font-bold">AI review: automatic decisions</p>
            {error && <p className="text-sm mt-1" style={{ color: '#b91c1c' }}>{error}</p>}
            {!form && !error && <p className="text-sm mt-1">Loading…</p>}
            {form && (
                <>
                    <Row title="Auto Approval" value={form.approve} onChange={(approve) => { setForm({ ...form, approve }); setNote(null); }}
                        hint="Approved automatically only when the AI approves every answer at or above this confidence and all points are decided." />
                    <Row title="Auto Rejection" value={form.reject} onChange={(reject) => { setForm({ ...form, reject }); setNote(null); }}
                        hint="Rejected automatically only when the AI rejects the submission at or above this confidence." />
                    {badThreshold && <p className="text-sm" style={{ color: '#b91c1c' }}>Threshold must be a whole number from {MIN} to {MAX}.</p>}
                    <p className="text-sm mt-1">If both are OFF, the AI still reviews every submission and shows its suggestion; you decide.</p>
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                        <button type="button" onClick={save} disabled={!changed || badThreshold || saving}
                            className="px-4 py-1.5 rounded text-white font-semibold disabled:opacity-40" style={{ backgroundColor: '#25a55f' }}>{saving ? 'Saving…' : 'Save'}</button>
                        {note && <span className="text-sm font-semibold" style={{ color: note.ok ? '#1e7d48' : '#b91c1c' }}>{note.text}</span>}
                        {saved?.updatedAt && <span className="text-xs text-gray-600">Last changed {fmt(saved.updatedAt)}{saved.updatedBy?.email ? ` by ${saved.updatedBy.email}` : saved.updatedBy?.name ? ` (${saved.updatedBy.name})` : ''}</span>}
                    </div>
                </>
            )}
        </div>
    );
};

export default AiReviewSettings;
